import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  ChatBubbleLeftEllipsisIcon,
  BellAlertIcon,
  CheckIcon,
  XMarkIcon,
  ChevronDownIcon,
  EyeIcon,
  EyeSlashIcon,
  ClipboardDocumentCheckIcon,
  DocumentCheckIcon,
} from '@heroicons/react/24/outline';
import { config } from '../config/environment';
import { endpoints } from '../config/endpoints';
import { getUserTimezone, formatDateOnlyInTimezone, isOverdue } from '../services/timezoneUtils';
import type { ActionItem } from '../services/pracmagicTypes';

// Small tooltip wrapper — shows label above on hover
const Tip: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="relative group/tip">
    {children}
    <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 rounded bg-gray-800 px-2 py-0.5 text-xs text-white whitespace-nowrap opacity-0 group-hover/tip:opacity-100 transition-opacity duration-150 z-20">
      {label}
    </span>
  </div>
);

interface Props {
  mode: 'by_appointment' | 'by_client';
  entityId: string | number;
  isCoach: boolean;
  onItemUpdated?: () => void;
  clientName?: string;
  coachIdFilter?: string | null;
  approvedOnly?: boolean;
  initialTypeFilter?: string;
  onNavigate?: (section: string) => void;
}

type StatusFilter = 'all' | 'pending' | 'in_progress' | 'completed' | 'cancelled';

const PRIORITY_COLORS: Record<string, string> = {
  high:   'bg-red-900/40 text-red-400',
  medium: 'bg-yellow-900/40 text-yellow-400',
  low:    'bg-green-900/40 text-green-400',
};

const STATUS_OPTIONS = [
  { value: 'pending',     label: 'Pending' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed',   label: 'Completed' },
  { value: 'cancelled',   label: 'Cancelled' },
];

const STATUS_COLORS: Record<string, string> = {
  pending:     'bg-blue-900/40 text-blue-400',
  in_progress: 'bg-indigo-900/40 text-indigo-400',
  completed:   'bg-green-900/40 text-green-400',
  cancelled:   'bg-gray-700/40 text-gray-400',
};

const todayISO = () => new Date().toISOString().split('T')[0];

const authHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('authToken') || ''}`,
});

// ─── Add Item Form ─────────────────────────────────────────────────────────

interface SessionOption { id: string; meeting_name: string; scheduled_at: string; }

interface AddFormProps {
  /** Pre-filled when mode=by_appointment */
  appointmentId?: string;
  /** Used to fetch session list when mode=by_client */
  clientId?: string;
  onSaved: () => void;
  onCancel: () => void;
}

const AddItemForm: React.FC<AddFormProps> = ({ appointmentId, clientId, onSaved, onCancel }) => {
  const [sessions, setSessions] = useState<SessionOption[]>([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [form, setForm] = useState({
    appointment_id: appointmentId || '',
    title: '',
    assigned_to: 'client',
    due_date: '',
    priority: 'medium',
    notes: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Fetch sessions when in by_client mode
  useEffect(() => {
    if (!clientId) return;
    setSessionsLoading(true);
    fetch(`${config.apiBaseUrl}/clients/${clientId}/sessions`, { headers: authHeaders() })
      .then(r => r.json())
      .then(data => {
        const appts: SessionOption[] = (data.appointments || []).map((a: any) => ({
          id: a.id,
          meeting_name: a.meeting_name,
          scheduled_at: a.scheduled_at,
        }));
        setSessions(appts);
        if (appts.length > 0 && !form.appointment_id) {
          setForm(p => ({ ...p, appointment_id: appts[0].id }));
        }
      })
      .catch(() => {})
      .finally(() => setSessionsLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title.trim()) { setError('Title is required'); return; }
    if (!form.appointment_id) { setError('Session is required'); return; }
    setSaving(true);
    setError('');
    try {
      const res = await fetch(`${config.apiBaseUrl}${endpoints.main.actionItems.create}`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          appointment_id: form.appointment_id,
          title: form.title.trim(),
          assigned_to: form.assigned_to,
          due_date: form.due_date || null,
          priority: form.priority,
          notes: form.notes || null,
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create action item');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="border border-dark-border bg-dark-hover rounded-lg p-4 space-y-3">
      <p className="text-sm font-semibold text-primary-300">New Action Item</p>

      {/* Session picker — only in by_client mode */}
      {clientId !== undefined && (
        <div>
          <label className="block text-xs text-gray-400 mb-1">Session</label>
          {sessionsLoading ? (
            <p className="text-xs text-gray-400">Loading sessions…</p>
          ) : sessions.length === 0 ? (
            <p className="text-xs text-gray-400">No sessions found.</p>
          ) : (
            <select
              value={form.appointment_id}
              onChange={e => setForm(p => ({ ...p, appointment_id: e.target.value }))}
              className="w-full text-sm border border-dark-border rounded-lg px-2 py-1.5 focus:ring-2 focus:ring-primary-500 bg-dark-canvas text-gray-200"
            >
              {sessions.map(s => (
                <option key={s.id} value={s.id}>
                  {s.meeting_name} — {new Date(s.scheduled_at).toLocaleDateString()}
                </option>
              ))}
            </select>
          )}
        </div>
      )}

      <div>
        <input
          type="text"
          placeholder="Title — start with an action verb (e.g. Schedule, Write, Practice…)"
          value={form.title}
          onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
          className="w-full text-sm border border-dark-border rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-dark-canvas text-gray-200"
          autoFocus
        />
      </div>

      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
        <div>
          <label className="block text-xs text-gray-400 mb-1">Assigned to</label>
          <select
            value={form.assigned_to}
            onChange={e => setForm(p => ({ ...p, assigned_to: e.target.value }))}
            className="w-full text-sm border border-dark-border rounded-lg px-2 py-1.5 focus:ring-2 focus:ring-primary-500 bg-dark-canvas text-gray-200"
          >
            <option value="client">Client</option>
            <option value="coach">Coach</option>
            <option value="both">Both</option>
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-400 mb-1">Priority</label>
          <select
            value={form.priority}
            onChange={e => setForm(p => ({ ...p, priority: e.target.value }))}
            className="w-full text-sm border border-dark-border rounded-lg px-2 py-1.5 focus:ring-2 focus:ring-primary-500 bg-dark-canvas text-gray-200"
          >
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-400 mb-1">Due date</label>
          <input
            type="date"
            value={form.due_date}
            onChange={e => setForm(p => ({ ...p, due_date: e.target.value }))}
            className="w-full text-sm border border-dark-border rounded-lg px-2 py-1.5 focus:ring-2 focus:ring-primary-500 bg-dark-canvas text-gray-200 [color-scheme:dark]"
          />
        </div>
      </div>

      <div>
        <label className="block text-xs text-gray-400 mb-1">Notes (optional)</label>
        <textarea
          value={form.notes}
          onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
          rows={2}
          className="w-full text-sm border border-dark-border rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 bg-dark-canvas text-gray-200 resize-none"
          placeholder="Any context or instructions…"
        />
      </div>

      {error && <p className="text-xs text-red-600">{error}</p>}

      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel}
          className="px-3 py-1.5 text-sm border border-dark-border rounded-lg text-gray-300 hover:bg-dark-canvas">
          Cancel
        </button>
        <button type="submit" disabled={saving}
          className={`px-3 py-1.5 text-sm rounded-lg text-white ${saving ? 'bg-gray-400' : 'bg-primary-600 hover:bg-primary-700'}`}>
          {saving ? 'Adding…' : 'Add Item'}
        </button>
      </div>
    </form>
  );
};

// ─── Main Component ────────────────────────────────────────────────────────

const ActionItemsPanel: React.FC<Props> = ({ mode, entityId, isCoach, onItemUpdated, clientName, coachIdFilter, approvedOnly, initialTypeFilter, onNavigate }) => {
  const [items, setItems]             = useState<ActionItem[]>([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [notification, setNotification] = useState<string | null>(null);

  // Per-item UI state
  const [editingId, setEditingId]       = useState<string | null>(null);
  const [notesOpenId, setNotesOpenId]   = useState<string | null>(null);
  const [nudgeOpenId, setNudgeOpenId]   = useState<string | null>(null);
  const [editDraft, setEditDraft]       = useState<Record<string, any>>({});
  const [notesDraft, setNotesDraft]     = useState('');
  const [nudgeDraft, setNudgeDraft]     = useState('');
  const [savingId, setSavingId]         = useState<string | null>(null);
  const [deletingId, setDeletingId]     = useState<string | null>(null);
  const [showAddForm, setShowAddForm]   = useState(false);
  const [approvingId, setApprovingId]   = useState<string | null>(null);
  const [approvingAll, setApprovingAll] = useState(false);
  const [sending, setSending]           = useState(false);
  const [sentConfirm, setSentConfirm]   = useState(false);

  const timezone = getUserTimezone();

  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  const [groupBy, setGroupBy]               = useState<'status' | 'type'>('type');
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [typeDropdownOpen, setTypeDropdownOpen]     = useState(false);
  const statusDropdownRef = useRef<HTMLDivElement>(null);
  const typeDropdownRef   = useRef<HTMLDivElement>(null);
  // Multiselect filters for by_client mode. 'completed' controls whether done items are shown.
  const [filterStatuses, setFilterStatuses] = useState<Set<string>>(new Set(['pending', 'in_progress']));
  const [filterTypes, setFilterTypes]       = useState<Set<string>>(initialTypeFilter ? new Set([initialTypeFilter]) : new Set());

  const toggleGroup = (key: string) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const notify = (msg: string) => {
    setNotification(msg);
    setTimeout(() => setNotification(null), 3000);
  };

  const fetchItems = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const endpoint = mode === 'by_appointment'
        ? endpoints.main.actionItems.bySession(entityId as string)
        : isCoach
          ? endpoints.main.actionItems.byClientCoach(entityId as string)
          : endpoints.main.actionItems.byClient(entityId as string);

      const res = await fetch(`${config.apiBaseUrl}${endpoint}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('authToken') || ''}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setItems(data.action_items || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load action items');
    } finally {
      setLoading(false);
    }
  }, [mode, entityId, isCoach]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(e.target as Node)) setStatusDropdownOpen(false);
      if (typeDropdownRef.current && !typeDropdownRef.current.contains(e.target as Node)) setTypeDropdownOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ── API helpers ──────────────────────────────────────────────────────────

  const apiUpdate = async (id: string, body: object) => {
    const res = await fetch(`${config.apiBaseUrl}${endpoints.main.actionItems.update(id)}`, {
      method: 'PUT',
      headers: authHeaders(),
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  };

  // ── Status change ────────────────────────────────────────────────────────

  const handleStatusChange = async (item: ActionItem, newStatus: string) => {
    // For completed status, auto-set today; user can override via Edit if needed
    setSavingId(item.id);
    try {
      const body: Record<string, any> = { status: newStatus };
      if (newStatus === 'completed') body.completed_at = todayISO();
      await apiUpdate(item.id, body);
      await fetchItems();
      onItemUpdated?.();
      notify('Status updated');
    } catch {
      notify('Failed to update status');
    } finally {
      setSavingId(null);
    }
  };

  // ── Approval ─────────────────────────────────────────────────────────────

  const handleApprove = async (item: ActionItem) => {
    setApprovingId(item.id);
    try {
      const res = await fetch(`${config.apiBaseUrl}${endpoints.main.actionItems.approve(item.id)}`, {
        method: 'POST',
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await fetchItems();
      notify(item.coach_approved ? 'Approval revoked' : 'Approved for client');
    } catch {
      notify('Failed to update approval');
    } finally {
      setApprovingId(null);
    }
  };

  const handleApproveAll = async () => {
    if (mode !== 'by_appointment') return;
    setApprovingAll(true);
    try {
      const res = await fetch(
        `${config.apiBaseUrl}${endpoints.main.session.approveAllActionItems(entityId as string)}`,
        { method: 'POST', headers: authHeaders() }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await fetchItems();
      notify('All action items approved for client');
    } catch {
      notify('Failed to approve all');
    } finally {
      setApprovingAll(false);
    }
  };

  const handleSendToClient = async () => {
    if (mode !== 'by_appointment') return;
    setSending(true);
    try {
      const res = await fetch(
        `${config.apiBaseUrl}${endpoints.main.actionItems.sendToClient(entityId as string)}`,
        { method: 'POST', headers: authHeaders() }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        notify(err.detail || 'Failed to send');
        return;
      }
      setSentConfirm(true);
      notify('Action items sent to client');
      setTimeout(() => setSentConfirm(false), 4000);
    } catch {
      notify('Failed to send');
    } finally {
      setSending(false);
    }
  };

  // ── Edit ─────────────────────────────────────────────────────────────────

  const startEdit = (item: ActionItem) => {
    setEditingId(item.id);
    setEditDraft({
      title:         item.title,
      priority:      item.priority,
      due_date:      item.due_date || '',
      completed_at:  item.completed_at ? item.completed_at.split('T')[0] : '',
      status:        item.status,
      nudge_context: item.nudge_context || '',
    });
    setNotesOpenId(null);
    setNudgeOpenId(null);
  };

  const cancelEdit = () => { setEditingId(null); setEditDraft({}); };

  const saveEdit = async (id: string) => {
    setSavingId(id);
    try {
      const body: Record<string, any> = {
        title:         editDraft.title,
        priority:      editDraft.priority,
        due_date:      editDraft.due_date || null,
        status:        editDraft.status,
        nudge_context: editDraft.nudge_context || null,
      };
      if (editDraft.status === 'completed') {
        body.completed_at = editDraft.completed_at || todayISO();
      }
      await apiUpdate(id, body);
      setEditingId(null);
      await fetchItems();
      onItemUpdated?.();
      notify('Action item updated');
    } catch {
      notify('Failed to save changes');
    } finally {
      setSavingId(null);
    }
  };

  // ── Notes ────────────────────────────────────────────────────────────────

  const openNotes = (item: ActionItem) => {
    setNotesOpenId(notesOpenId === item.id ? null : item.id);
    setNotesDraft(item.notes || '');
    setEditingId(null);
    setNudgeOpenId(null);
  };

  const openNudge = (item: ActionItem) => {
    setNudgeOpenId(nudgeOpenId === item.id ? null : item.id);
    setNudgeDraft(item.nudge_context || '');
    setEditingId(null);
    setNotesOpenId(null);
  };

  const saveNudge = async (id: string) => {
    setSavingId(id);
    try {
      await apiUpdate(id, { nudge_context: nudgeDraft || null });
      setNudgeOpenId(null);
      await fetchItems();
      onItemUpdated?.();
      notify('Nudge reminder updated');
    } catch {
      notify('Failed to save nudge');
    } finally {
      setSavingId(null);
    }
  };

  const saveNotes = async (id: string) => {
    setSavingId(id);
    try {
      await apiUpdate(id, { notes: notesDraft });
      setNotesOpenId(null);
      await fetchItems();
      onItemUpdated?.();
      notify('Notes saved');
    } catch {
      notify('Failed to save notes');
    } finally {
      setSavingId(null);
    }
  };

  // ── Delete ────────────────────────────────────────────────────────────────

  const handleDelete = async (item: ActionItem) => {
    if (!window.confirm(`Delete "${item.title}"?`)) return;
    setDeletingId(item.id);
    try {
      const res = await fetch(`${config.apiBaseUrl}${endpoints.main.actionItems.delete(item.id)}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await fetchItems();
      onItemUpdated?.();
      notify('Action item deleted');
    } catch {
      notify('Failed to delete action item');
    } finally {
      setDeletingId(null);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────

  // Filtered item set for by_client mode (applies approvedOnly + coach filter + multiselect filters)
  const clientVisibleItems = useMemo(() => {
    let result = approvedOnly
      ? items.filter(i => (i as any).action_item_type === 'task' ? i.coach_approved : true)
      : [...items];
    if (coachIdFilter) {
      result = result.filter(i => (i as any).coach_id === coachIdFilter);
    }
    if (filterTypes.size > 0) {
      result = result.filter(i => filterTypes.has((i as any).action_item_type || 'task'));
    }
    result = result.filter(i => {
      const isDone = i.status === 'completed' || i.status === 'cancelled';
      if (isDone) return filterStatuses.has('completed');
      return filterStatuses.has(i.status);
    });
    return result;
  }, [items, approvedOnly, coachIdFilter, filterTypes, filterStatuses]);

  const filteredItems = items.filter(item =>
    (statusFilter === 'all' ? true : item.status === statusFilter) &&
    (!coachIdFilter || (item as any).coach_id === coachIdFilter)
  );

  // Group filtered items by session (appointment_id), sorted newest first.
  // Each group label shows the session name + date from scheduled_at when available.
  const groupedItems = useMemo(() => {
    const groups = new Map<string, { label: string; sortKey: string; items: ActionItem[] }>();
    for (const item of filteredItems) {
      const key = item.appointment_id;
      if (!groups.has(key)) {
        const label = item.scheduled_at
          ? `${item.meeting_name || 'Session'} · ${formatDateOnlyInTimezone(item.scheduled_at, timezone)}`
          : item.meeting_name || 'Session';
        const sortKey = item.scheduled_at || item.created_at;
        groups.set(key, { label, sortKey, items: [] });
      }
      groups.get(key)!.items.push(item);
    }
    return Array.from(groups.values()).sort((a, b) => b.sortKey.localeCompare(a.sortKey));
  }, [filteredItems, timezone]);

  // For by_client mode: group by status bucket instead of by session
  const statusGroups = useMemo(() => {
    if (mode !== 'by_client') return null;
    const active          = clientVisibleItems.filter(i => i.status !== 'completed' && i.status !== 'cancelled');
    const overdueItems    = active.filter(i => !!(i.due_date && isOverdue(i.due_date, timezone)));
    const overdueIds      = new Set(overdueItems.map(i => i.id));
    const pendingItems    = active.filter(i => i.status === 'pending'     && !overdueIds.has(i.id));
    const inProgressItems = active.filter(i => i.status === 'in_progress' && !overdueIds.has(i.id));
    const doneItems       = clientVisibleItems.filter(i => i.status === 'completed' || i.status === 'cancelled');
    return {
      groups: [
        { key: 'overdue',     label: 'Overdue',     items: overdueItems,     accent: 'text-red-400' },
        { key: 'pending',     label: 'Pending',      items: pendingItems,     accent: 'text-amber-400' },
        { key: 'in_progress', label: 'In Progress',  items: inProgressItems,  accent: 'text-blue-400' },
      ].filter(g => g.items.length > 0),
      done: doneItems,
    };
  }, [mode, clientVisibleItems, timezone]);

  // For by_client mode: group by action_item_type
  const typeGroups = useMemo(() => {
    if (mode !== 'by_client') return null;
    const TYPES = [
      { key: 'task',     label: 'Tasks' },
      { key: 'form',     label: 'Forms' },
      { key: 'check-in', label: 'Check-ins' },
      { key: 'document', label: 'Documents' },
    ] as const;
    return TYPES.map(({ key, label }) => ({
      key,
      label,
      items: clientVisibleItems.filter(i => (i as any).action_item_type === key || (!(i as any).action_item_type && key === 'task')),
    })).filter(g => g.items.length > 0);
  }, [mode, clientVisibleItems]);

  // Derive props for AddItemForm
  const addFormProps = mode === 'by_appointment'
    ? { appointmentId: entityId as string }
    : { clientId: entityId as string };

  return (
    <div className="relative">
      {/* Toast */}
      {notification && (
        <div className="fixed top-4 right-4 z-50 bg-gray-900 text-white px-4 py-2 rounded-lg text-sm shadow-lg">
          {notification}
        </div>
      )}

      {/* Toolbar */}
      <div className="mb-4 flex flex-col gap-2">
        {/* by_client: group-by + multiselect filters */}
        {mode === 'by_client' && (
          <div className="flex flex-wrap items-center gap-4">
            {/* Group by — radio buttons */}
            <div className="flex items-center gap-3">
              <span className="text-xs text-gray-500 shrink-0">Group by:</span>
              {(['type', 'status'] as const).map(opt => (
                <label key={opt} className="flex items-center gap-1.5 cursor-pointer">
                  <input
                    type="radio"
                    name="actionItemGroupBy"
                    value={opt}
                    checked={groupBy === opt}
                    onChange={() => setGroupBy(opt)}
                    className="accent-primary-500 cursor-pointer"
                  />
                  <span className="text-xs text-gray-300">{opt === 'status' ? 'Status' : 'Type'}</span>
                </label>
              ))}
            </div>

            {/* Status multiselect dropdown */}
            <div className="relative" ref={statusDropdownRef}>
              <button
                onClick={() => { setStatusDropdownOpen(p => !p); setTypeDropdownOpen(false); }}
                className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-dark-hover text-gray-300 hover:text-white border border-dark-border transition-colors"
              >
                <span>
                  {filterStatuses.size === 0
                    ? 'Status: None'
                    : filterStatuses.size === 3
                    ? 'Status: All'
                    : `Status: ${[
                        filterStatuses.has('pending')     && 'Pending',
                        filterStatuses.has('in_progress') && 'In Progress',
                        filterStatuses.has('completed')   && 'Done',
                      ].filter(Boolean).join(', ')}`}
                </span>
                <ChevronDownIcon className={`w-3 h-3 transition-transform ${statusDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              {statusDropdownOpen && (
                <div className="absolute top-full left-0 mt-1 z-20 bg-dark-canvas border border-dark-border rounded-lg shadow-lg py-1 min-w-[140px]">
                  {([
                    { key: 'pending',     label: 'Pending' },
                    { key: 'in_progress', label: 'In Progress' },
                    { key: 'completed',   label: 'Done' },
                  ] as const).map(({ key, label }) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setFilterStatuses(prev => {
                        const next = new Set(prev);
                        if (next.has(key)) next.delete(key); else next.add(key);
                        return next;
                      })}
                      className="w-full flex items-center gap-2 px-3 py-1.5 hover:bg-dark-hover text-left"
                    >
                      <span className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${filterStatuses.has(key) ? 'bg-primary-500 border-primary-500' : 'border-gray-500 bg-transparent'}`}>
                        {filterStatuses.has(key) && <CheckIcon className="w-3 h-3 text-white" />}
                      </span>
                      <span className="text-xs text-gray-300">{label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Type multiselect dropdown */}
            <div className="relative" ref={typeDropdownRef}>
              <button
                onClick={() => { setTypeDropdownOpen(p => !p); setStatusDropdownOpen(false); }}
                className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-dark-hover text-gray-300 hover:text-white border border-dark-border transition-colors"
              >
                <span>
                  {filterTypes.size === 0
                    ? 'Type: All'
                    : `Type: ${[
                        filterTypes.has('task')      && 'Tasks',
                        filterTypes.has('form')      && 'Forms',
                        filterTypes.has('check-in')  && 'Check-ins',
                        filterTypes.has('document')  && 'Docs',
                      ].filter(Boolean).join(', ')}`}
                </span>
                <ChevronDownIcon className={`w-3 h-3 transition-transform ${typeDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              {typeDropdownOpen && (
                <div className="absolute top-full left-0 mt-1 z-20 bg-dark-canvas border border-dark-border rounded-lg shadow-lg py-1 min-w-[140px]">
                  {([
                    { key: 'task',     label: 'Tasks' },
                    { key: 'form',     label: 'Forms' },
                    { key: 'check-in', label: 'Check-ins' },
                    { key: 'document', label: 'Docs' },
                  ] as const).map(({ key, label }) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setFilterTypes(prev => {
                        const next = new Set(prev);
                        if (next.has(key)) next.delete(key); else next.add(key);
                        return next;
                      })}
                      className="w-full flex items-center gap-2 px-3 py-1.5 hover:bg-dark-hover text-left"
                    >
                      <span className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${filterTypes.has(key) ? 'bg-primary-500 border-primary-500' : 'border-gray-500 bg-transparent'}`}>
                        {filterTypes.has(key) && <CheckIcon className="w-3 h-3 text-white" />}
                      </span>
                      <span className="text-xs text-gray-300">{label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Filter row — only shown in by_appointment mode; by_client uses chips above */}
        {mode !== 'by_client' && (
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600 font-medium shrink-0">Status:</label>
            <select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value as StatusFilter)}
              className="flex-1 sm:flex-none text-sm border border-dark-border rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-primary-500 bg-dark-canvas text-gray-200"
            >
              <option value="all">All</option>
              <option value="pending">Pending</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
        )}

        {/* Action buttons row */}
        <div className="flex items-center gap-2 flex-wrap">
          {isCoach && mode === 'by_appointment' && (
            items.length > 0 && items.every(i => i.coach_approved)
              ? (
                <span className="flex items-center gap-1.5 text-sm text-green-400 px-3 py-1.5 rounded-lg border border-green-700/40 bg-green-900/20 font-medium cursor-default">
                  <EyeIcon className="w-4 h-4" />
                  All Approved
                </span>
              ) : (
                <button
                  onClick={handleApproveAll}
                  disabled={approvingAll}
                  title="Approve all action items for client"
                  className="flex items-center gap-1.5 text-sm bg-green-700 hover:bg-green-600 text-white px-3 py-1.5 rounded-lg font-medium disabled:opacity-50"
                >
                  <EyeIcon className="w-4 h-4" />
                  {approvingAll ? 'Approving…' : 'Approve All'}
                </button>
              )
          )}

          {isCoach && mode === 'by_appointment' && (
            sentConfirm ? (
              <span className="flex items-center gap-1.5 text-sm text-blue-400 px-3 py-1.5 rounded-lg border border-blue-700/40 bg-blue-900/20 font-medium">
                Sent!
              </span>
            ) : (
              <button
                onClick={handleSendToClient}
                disabled={sending || !items.some(i => i.coach_approved)}
                title={items.some(i => i.coach_approved) ? 'Email approved action items to client' : 'Approve at least one item first'}
                className="flex items-center gap-1.5 text-sm bg-blue-700 hover:bg-blue-600 text-white px-3 py-1.5 rounded-lg font-medium disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {sending ? 'Sending…' : 'Send to Client'}
              </button>
            )
          )}

          <button
            onClick={() => setShowAddForm(p => !p)}
            className="flex items-center gap-1.5 text-sm bg-primary-600 hover:bg-primary-700 text-white px-3 py-1.5 rounded-lg font-medium"
          >
            <PlusIcon className="w-4 h-4" />
            Add Item
          </button>
        </div>
      </div>

      {/* Add form */}
      {showAddForm && (
        <div className="mb-4">
          <AddItemForm
            {...addFormProps}
            onSaved={() => {
              setShowAddForm(false);
              fetchItems();
              onItemUpdated?.();
              notify('Action item added');
            }}
            onCancel={() => setShowAddForm(false)}
          />
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
        </div>
      )}

      {/* Error */}
      {!loading && error && (
        <div className="text-center py-8 text-red-500">
          <p>{error}</p>
          <button onClick={fetchItems} className="mt-2 text-sm text-primary-600 hover:underline">Retry</button>
        </div>
      )}

      {/* Item list */}
      {!loading && !error && (() => {
        const renderItemCard = (item: ActionItem) => {
            const overdue    = !!(item.due_date && item.status !== 'completed' && item.status !== 'cancelled' && isOverdue(item.due_date, timezone));
            const isEditing  = editingId  === item.id;
            const notesOpen  = notesOpenId === item.id;
            const nudgeOpen  = nudgeOpenId === item.id;
            const isSaving   = savingId   === item.id;

            const cardBg = item.status === 'completed'
              ? 'border-green-700/50 bg-green-950'
              : item.status === 'in_progress'
              ? 'border-amber-700/50 bg-amber-950'
              : 'border-dark-border bg-dark-canvas';

            return (
              <div
                key={item.id}
                className={`border rounded-lg transition-colors ${cardBg}`}
              >
                {/* ── Main row ── */}
                <div className="p-4">
                  {isEditing ? (
                    /* Edit mode */
                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs text-gray-400 mb-1">Title</label>
                        <input
                          type="text"
                          value={editDraft.title || ''}
                          onChange={e => setEditDraft(p => ({ ...p, title: e.target.value }))}
                          className="w-full text-sm border border-dark-border rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 bg-dark-canvas text-gray-200"
                          autoFocus
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                        <div>
                          <label className="block text-xs text-gray-400 mb-1">Status</label>
                          <select
                            value={editDraft.status || 'pending'}
                            onChange={e => setEditDraft(p => ({ ...p, status: e.target.value }))}
                            className="w-full text-sm border border-dark-border rounded-lg px-2 py-1.5 focus:ring-2 focus:ring-primary-500 bg-dark-canvas text-gray-200"
                          >
                            {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs text-gray-400 mb-1">Priority</label>
                          <select
                            value={editDraft.priority || 'medium'}
                            onChange={e => setEditDraft(p => ({ ...p, priority: e.target.value }))}
                            className="w-full text-sm border border-dark-border rounded-lg px-2 py-1.5 focus:ring-2 focus:ring-primary-500 bg-dark-canvas text-gray-200"
                          >
                            <option value="low">Low</option>
                            <option value="medium">Medium</option>
                            <option value="high">High</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs text-gray-400 mb-1">Due date</label>
                          <input
                            type="date"
                            value={editDraft.due_date || ''}
                            onChange={e => setEditDraft(p => ({ ...p, due_date: e.target.value }))}
                            className="w-full text-sm border border-dark-border rounded-lg px-2 py-1.5 focus:ring-2 focus:ring-primary-500 bg-dark-canvas text-gray-200 [color-scheme:dark]"
                          />
                        </div>
                        {/* Completion date — only when status is completed */}
                        {editDraft.status === 'completed' && (
                          <div>
                            <label className="block text-xs text-gray-400 mb-1">Completed on</label>
                            <input
                              type="date"
                              value={editDraft.completed_at || todayISO()}
                              onChange={e => setEditDraft(p => ({ ...p, completed_at: e.target.value }))}
                              className="w-full text-sm border border-dark-border rounded-lg px-2 py-1.5 focus:ring-2 focus:ring-primary-500 bg-dark-canvas text-gray-200 [color-scheme:dark]"
                            />
                          </div>
                        )}
                      </div>

                      <div>
                        <label className="block text-xs text-gray-400 mb-1">Nudge reminder</label>
                        <input
                          type="text"
                          value={editDraft.nudge_context || ''}
                          onChange={e => setEditDraft(p => ({ ...p, nudge_context: e.target.value }))}
                          placeholder="A warm, personal motivational sentence (max 25 words)…"
                          className="w-full text-sm border border-dark-border rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 bg-dark-canvas text-gray-200"
                        />
                      </div>

                      <div className="flex gap-2 justify-end">
                        <button onClick={cancelEdit}
                          className="flex items-center gap-1 px-3 py-1.5 text-xs border border-dark-border rounded-lg text-gray-300 hover:bg-dark-canvas">
                          <XMarkIcon className="w-3.5 h-3.5" /> Cancel
                        </button>
                        <button onClick={() => saveEdit(item.id)} disabled={isSaving}
                          className={`flex items-center gap-1 px-3 py-1.5 text-xs rounded-lg text-white ${isSaving ? 'bg-gray-400' : 'bg-primary-600 hover:bg-primary-700'}`}>
                          <CheckIcon className="w-3.5 h-3.5" /> {isSaving ? 'Saving…' : 'Save'}
                        </button>
                      </div>
                    </div>
                  ) : (
                    /* View mode */
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:gap-3">
                      {/* Title + meta */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start gap-2 flex-wrap mb-1.5">
                          <span className="text-sm font-medium text-gray-100">{item.title}</span>
                          {overdue && (
                            <span className="px-1.5 py-0.5 rounded-full text-xs font-medium bg-red-900/40 text-red-400 flex-shrink-0">
                              Overdue
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 flex-wrap text-xs text-gray-400">
                          {item.status === 'completed' && item.completed_at ? (
                            <span>Completed: {formatDateOnlyInTimezone(item.completed_at, timezone)}</span>
                          ) : (
                            <span
                              className={item.due_date ? '' : 'text-gray-300 cursor-pointer hover:text-violet-500'}
                              onClick={!item.due_date ? () => startEdit(item) : undefined}
                              title={!item.due_date ? 'Click to set due date' : undefined}
                            >
                              {item.due_date
                                ? `Due: ${formatDateOnlyInTimezone(item.due_date, timezone)}`
                                : 'No due date'}
                            </span>
                          )}
                          <span className={`px-2 py-0.5 rounded-full font-medium ${PRIORITY_COLORS[item.priority] || 'bg-gray-100 text-gray-700'}`}>
                            {item.priority}
                          </span>
                          <span className="italic">
                            {item.assigned_to === 'client'
                              ? (clientName || 'Client')
                              : item.assigned_to === 'both'
                              ? `Coach & ${clientName || 'Client'}`
                              : 'Coach'}
                          </span>
                          {item.notes && (
                            <span className="text-primary-600 font-medium">has notes</span>
                          )}
                        </div>
                      </div>

                      {/* Controls — full-width row on mobile, right-aligned column on sm+ */}
                      <div className="flex items-center gap-1.5 flex-wrap sm:flex-nowrap sm:flex-shrink-0">
                        {/* Status dropdown */}
                        <select
                          value={item.status}
                          onChange={e => handleStatusChange(item, e.target.value)}
                          disabled={isSaving}
                          className={`text-xs px-2 py-1.5 rounded-full font-medium border-0 cursor-pointer focus:ring-2 focus:ring-primary-500 ${STATUS_COLORS[item.status] || 'bg-gray-700/40 text-gray-400'}`}
                        >
                          {STATUS_OPTIONS.map(o => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                          ))}
                        </select>

                        {/* Nudge */}
                        <Tip label="Nudge reminder">
                          <button onClick={() => openNudge(item)}
                            className={`p-2 rounded-lg transition-colors ${
                              nudgeOpen
                                ? 'bg-amber-900/40 text-amber-400'
                                : item.nudge_context
                                  ? 'text-amber-500 hover:bg-amber-900/20'
                                  : 'text-gray-400 hover:text-gray-500 hover:bg-dark-hover'
                            }`}>
                            <BellAlertIcon className="w-4 h-4" />
                          </button>
                        </Tip>

                        {/* Notes */}
                        <Tip label="Notes">
                          <button onClick={() => openNotes(item)}
                            className={`p-2 rounded-lg transition-colors ${notesOpen ? 'bg-primary-900/40 text-primary-400' : 'text-gray-400 hover:text-gray-300 hover:bg-dark-hover'}`}>
                            <ChatBubbleLeftEllipsisIcon className="w-4 h-4" />
                          </button>
                        </Tip>

                        {/* Fill check-in — clients only */}
                        {!isCoach && (item as any).action_item_type === 'check-in' && onNavigate && (
                          <Tip label="Fill Check-in">
                            <button onClick={() => onNavigate('check-ins')}
                              className="p-2 rounded-lg text-primary-400 hover:text-primary-300 hover:bg-primary-900/20 transition-colors">
                              <ClipboardDocumentCheckIcon className="w-4 h-4" />
                            </button>
                          </Tip>
                        )}

                        {/* Sign document — clients only */}
                        {!isCoach && (item as any).action_item_type === 'document' && onNavigate && (
                          <Tip label="Sign Document">
                            <button onClick={() => onNavigate('documents')}
                              className="p-2 rounded-lg text-primary-400 hover:text-primary-300 hover:bg-primary-900/20 transition-colors">
                              <DocumentCheckIcon className="w-4 h-4" />
                            </button>
                          </Tip>
                        )}

                        {/* Edit — hidden for clients on check-ins and documents */}
                        {(isCoach || !['check-in', 'document'].includes((item as any).action_item_type)) && (
                          <Tip label="Edit">
                            <button onClick={() => startEdit(item)}
                              className="p-2 rounded-lg text-gray-400 hover:text-gray-300 hover:bg-dark-hover transition-colors">
                              <PencilIcon className="w-4 h-4" />
                            </button>
                          </Tip>
                        )}

                        {/* Delete */}
                        <Tip label="Delete">
                          <button onClick={() => handleDelete(item)} disabled={deletingId === item.id}
                            className="p-2 rounded-lg text-gray-400 hover:text-red-400 hover:bg-red-900/20 transition-colors">
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        </Tip>

                        {/* Approve for client */}
                        {isCoach && (
                          <Tip label={item.coach_approved ? 'Revoke client visibility' : 'Approve for client'}>
                            <button
                              onClick={() => handleApprove(item)}
                              disabled={approvingId === item.id}
                              className={`p-2 rounded-lg transition-colors disabled:opacity-50 ${
                                item.coach_approved
                                  ? 'text-green-400 bg-green-900/30 hover:bg-green-900/50'
                                  : 'text-gray-500 hover:text-green-400 hover:bg-green-900/20'
                              }`}
                            >
                              {item.coach_approved
                                ? <EyeIcon className="w-4 h-4" />
                                : <EyeSlashIcon className="w-4 h-4" />
                              }
                            </button>
                          </Tip>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* ── Nudge panel ── */}
                {nudgeOpen && (
                  <div className="px-4 pb-4 border-t border-amber-900/40 pt-3 bg-amber-950/20 rounded-b-lg">
                    <div className="flex items-center gap-1.5 mb-2">
                      <BellAlertIcon className="w-3.5 h-3.5 text-amber-400" />
                      <label className="text-xs font-medium text-amber-300">Nudge Reminder</label>
                    </div>
                    <input
                      type="text"
                      value={nudgeDraft}
                      onChange={e => setNudgeDraft(e.target.value)}
                      placeholder="A warm, personal motivational sentence (max 25 words)…"
                      className="w-full text-sm border border-dark-border rounded-lg px-3 py-2 focus:ring-2 focus:ring-amber-400 bg-dark-canvas text-gray-200"
                      autoFocus
                    />
                    <div className="flex justify-end gap-2 mt-2">
                      <button onClick={() => setNudgeOpenId(null)}
                        className="px-3 py-1.5 text-xs border border-dark-border rounded-lg text-gray-300 hover:bg-dark-canvas">
                        Cancel
                      </button>
                      <button onClick={() => saveNudge(item.id)} disabled={isSaving}
                        className={`px-3 py-1.5 text-xs rounded-lg text-white ${isSaving ? 'bg-gray-400' : 'bg-amber-600 hover:bg-amber-700'}`}>
                        {isSaving ? 'Saving…' : 'Save Nudge'}
                      </button>
                    </div>
                  </div>
                )}

                {/* ── Notes panel ── */}
                {notesOpen && (
                  <div className="px-4 pb-4 border-t border-dark-border pt-3">
                    <label className="block text-xs font-medium text-gray-400 mb-1.5">
                      Notes / Comments
                    </label>
                    <textarea
                      value={notesDraft}
                      onChange={e => setNotesDraft(e.target.value)}
                      rows={3}
                      placeholder="Add context, progress updates, or observations…"
                      className="w-full text-sm border border-dark-border rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 resize-none bg-dark-canvas text-gray-200"
                      autoFocus
                    />
                    <div className="flex justify-end gap-2 mt-2">
                      <button onClick={() => setNotesOpenId(null)}
                        className="px-3 py-1.5 text-xs border border-dark-border rounded-lg text-gray-300 hover:bg-dark-canvas">
                        Cancel
                      </button>
                      <button onClick={() => saveNotes(item.id)} disabled={isSaving}
                        className={`px-3 py-1.5 text-xs rounded-lg text-white ${isSaving ? 'bg-gray-400' : 'bg-primary-600 hover:bg-primary-700'}`}>
                        {isSaving ? 'Saving…' : 'Save Notes'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
        };

        // ── by_client: type-grouped layout ───────────────────────────────────
        if (mode === 'by_client' && groupBy === 'type' && typeGroups) {
          if (typeGroups.length === 0) {
            return (
              <div className="text-center py-12 text-gray-400">
                <p>No action items match the current filters.</p>
              </div>
            );
          }
          return (
            <div className="space-y-4">
              {typeGroups.map(group => {
                const isCollapsed = collapsedGroups.has(`type-${group.key}`);
                return (
                  <div key={group.key}>
                    <button
                      onClick={() => toggleGroup(`type-${group.key}`)}
                      className="w-full flex items-center gap-2 py-1.5 px-1 text-left mb-2 border-b border-dark-border"
                    >
                      <ChevronDownIcon className={`w-4 h-4 flex-shrink-0 transition-transform ${isCollapsed ? '-rotate-90' : ''} text-primary-400`} />
                      <span className="text-sm font-semibold text-primary-300 truncate">{group.label}</span>
                      <span className="ml-auto text-xs text-gray-600 flex-shrink-0">
                        {group.items.length} item{group.items.length !== 1 ? 's' : ''}
                      </span>
                    </button>
                    {!isCollapsed && (
                      <div className="space-y-2">
                        {group.items.map(item => renderItemCard(item))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        }

        // ── by_client: status-grouped layout ─────────────────────────────────
        if (mode === 'by_client' && statusGroups) {
          const activeEmpty = statusGroups.groups.length === 0;
          return (
            <div className="space-y-4">
              {activeEmpty && statusGroups.done.length === 0 && (
                <div className="text-center py-12 text-gray-400">
                  <p>No action items yet.</p>
                </div>
              )}
              {statusGroups.groups.map(group => (
                <div key={group.key}>
                  <button
                    onClick={() => toggleGroup(group.key)}
                    className="w-full flex items-center gap-2 py-1.5 px-1 text-left mb-2 border-b border-dark-border"
                  >
                    <ChevronDownIcon className={`w-4 h-4 flex-shrink-0 transition-transform ${collapsedGroups.has(group.key) ? '-rotate-90' : ''} ${group.accent}`} />
                    <span className={`text-sm font-semibold ${group.accent} truncate`}>{group.label}</span>
                    <span className="ml-auto text-xs text-gray-600 flex-shrink-0">
                      {group.items.length} item{group.items.length !== 1 ? 's' : ''}
                    </span>
                  </button>
                  {!collapsedGroups.has(group.key) && (
                    <div className="space-y-2">
                      {group.items.map(item => renderItemCard(item))}
                    </div>
                  )}
                </div>
              ))}

              {/* Completed / cancelled — shown only when Done status filter is active */}
              {statusGroups.done.length > 0 && (
                <div className="mt-2 pl-2 border-l-2 border-dark-border space-y-2">
                  {statusGroups.done.map(item => renderItemCard(item))}
                </div>
              )}
            </div>
          );
        }

        // ── by_appointment: session-grouped layout ────────────────────────────
        if (filteredItems.length === 0) {
          return (
            <div className="text-center py-12 text-gray-400">
              <p>No action items{statusFilter !== 'all' ? ` with status "${statusFilter}"` : ''}.</p>
            </div>
          );
        }
        return (
          <div className="space-y-4">
            {groupedItems.map(group => (
              <div key={group.sortKey}>
                <button
                  onClick={() => toggleGroup(group.sortKey)}
                  className="w-full flex items-center gap-2 py-1.5 px-1 text-left mb-2 border-b border-dark-border"
                >
                  <ChevronDownIcon className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform ${collapsedGroups.has(group.sortKey) ? '-rotate-90' : ''}`} />
                  <span className="text-sm font-semibold text-gray-500 truncate">{group.label}</span>
                  <span className="ml-auto text-xs text-gray-600 flex-shrink-0">
                    {group.items.length} item{group.items.length !== 1 ? 's' : ''}
                  </span>
                </button>
                {!collapsedGroups.has(group.sortKey) && (
                  <div className="space-y-2">
                    {group.items.map(item => renderItemCard(item))}
                  </div>
                )}
              </div>
            ))}
          </div>
        );
      })()}
    </div>
  );
};

export default ActionItemsPanel;
